import React, { useState, useEffect } from 'react';
import { Card } from '../ui/card';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  Search,
  Download,
  RefreshCw,
  CheckCircle,
  Circle,
  Eye,
  MessageSquare,
  ArrowUp,
  ArrowDown,
  ChevronsUpDown
} from 'lucide-react';
import { adminAPI } from '../../services/api';
import { useToast } from '../ui/use-toast';
import CompanyRemarksModal from './CompanyRemarksModal';

export default function CompanyProgressTable({ onViewDetails }) {
  const [companies, setCompanies] = useState([]);
  const [filteredCompanies, setFilteredCompanies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [progressFilter, setProgressFilter] = useState('all');
  const [roleFilter, setRoleFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [remarksModalOpen, setRemarksModalOpen] = useState(false);
  const [selectedCompanyForRemarks, setSelectedCompanyForRemarks] = useState(null);
  const [columnSort, setColumnSort] = useState({}); // { columnKey: 'incomplete' | 'complete' | null }
  const { toast } = useToast();

  // Define all possible categories
  const categories = [
    'Fintech Startup of the Year',
    'Most Innovative Fintech Product',
    'Best Innovation in RegTech',
    'Best Use of AI in Fintech',
    'Best Initiative for Financial Literacy',
    'Fintech Scaleup of the Year',
    'Best Innovation in WealthTech',
    'Excellence in Payments',
    'Excellence in InsurTech',
    'Best Innovation in Digital Lending',
    'Best Technology Company for Fintech Solutions',
    'Best Fintech Initiative For Social Impact',
    'Best International Fintech in India'
  ];

  const taskColumns = [
    { key: 'company_details', label: 'Intake', short: 'Details' },
    { key: 'networking_passes', label: 'Passes', short: 'Passes' },
    { key: 'pitch_submission', label: 'Pitch', short: 'Pitch' },
    { key: 'brand_promotion', label: 'Brand Promotion', short: 'Brand' },
    { key: 'promotional_flyer', label: 'Flyer', short: 'Flyer' },
    { key: 'fo_nominations', label: 'FO', short: 'FO' },
  ];

  // Special column for Progress percentage
  const progressColumn = { key: 'progress', label: 'Progress' };

  useEffect(() => {
    fetchCompaniesProgress();
  }, []);

  useEffect(() => {
    filterCompanies();
  }, [searchTerm, progressFilter, roleFilter, categoryFilter, companies, columnSort]);

  const fetchCompaniesProgress = async () => {
    setLoading(true);
    try {
      const response = await adminAPI.getCompaniesWithProgress();
      setCompanies(response.data.companies);
    } catch (error) {
      console.error('Error fetching companies progress:', error);
      toast({
        title: 'Error',
        description: 'Failed to fetch companies progress',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const filterCompanies = () => {
    let filtered = [...companies];

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(company =>
        company.companyName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        company.pocEmail.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Progress filter
    if (progressFilter !== 'all') {
      if (progressFilter === 'completed') {
        filtered = filtered.filter(c => c.progressPercentage === 100);
      } else if (progressFilter === 'in-progress') {
        filtered = filtered.filter(c => c.progressPercentage > 0 && c.progressPercentage < 100);
      } else if (progressFilter === 'not-started') {
        filtered = filtered.filter(c => c.progressPercentage === 0);
      }
    }

    // Role filter
    if (roleFilter !== 'all') {
      filtered = filtered.filter(c => c.role === roleFilter);
    }

    // Category filter - check if company has the selected category
    if (categoryFilter !== 'all') {
      filtered = filtered.filter(company => {
        // Handle companies with no categories
        if (!company.category || !Array.isArray(company.category)) {
          return false;
        }
        // Check if any of the company's categories match the filter
        return company.category.includes(categoryFilter);
      });
    }

    // Column sorting - apply if any column is being sorted
    const sortedColumn = Object.keys(columnSort).find(key => columnSort[key] !== null);
    if (sortedColumn) {
      const sortOrder = columnSort[sortedColumn]; // 'incomplete' or 'complete'

      filtered.sort((a, b) => {
        // Special handling for progress column
        if (sortedColumn === 'progress') {
          const progressA = a.progressPercentage || 0;
          const progressB = b.progressPercentage || 0;

          if (sortOrder === 'incomplete') {
            // Show lower progress first (ascending order)
            return progressA - progressB;
          } else {
            // Show higher progress first (descending order)
            return progressB - progressA;
          }
        }

        // Task column sorting
        // Check if task is applicable for each company
        const isApplicableA = a.applicableTasks?.includes(sortedColumn) ?? true;
        const isApplicableB = b.applicableTasks?.includes(sortedColumn) ?? true;

        // Get task completion status
        const statusA = a.tasks[sortedColumn] || false;
        const statusB = b.tasks[sortedColumn] || false;

        if (sortOrder === 'incomplete') {
          // Show incomplete tasks first
          // Non-applicable tasks (shown as -) should go to the end
          if (!isApplicableA && isApplicableB) return 1;
          if (isApplicableA && !isApplicableB) return -1;
          if (!isApplicableA && !isApplicableB) return 0;

          // Among applicable tasks, incomplete comes first
          if (!statusA && statusB) return -1;
          if (statusA && !statusB) return 1;
          return 0;
        } else {
          // Show complete tasks first
          // Non-applicable tasks (shown as -) should go to the end
          if (!isApplicableA && isApplicableB) return 1;
          if (isApplicableA && !isApplicableB) return -1;
          if (!isApplicableA && !isApplicableB) return 0;

          // Among applicable tasks, complete comes first
          if (statusA && !statusB) return -1;
          if (!statusA && statusB) return 1;
          return 0;
        }
      });
    }

    setFilteredCompanies(filtered);
  };

  // Handle column header click for sorting
  const handleColumnSort = (columnKey) => {
    setColumnSort(prev => {
      const currentSort = prev[columnKey];

      // Three-state cycle: null -> incomplete -> complete -> null
      let newSort;
      if (!currentSort) {
        newSort = 'incomplete'; // First click: show incomplete first
      } else if (currentSort === 'incomplete') {
        newSort = 'complete'; // Second click: show complete first
      } else {
        newSort = null; // Third click: reset to original
      }

      // Clear other column sorts and set this one
      return { [columnKey]: newSort };
    });
  };

  // Get sort icon for column header
  const getSortIcon = (columnKey) => {
    const sortState = columnSort[columnKey];
    if (!sortState) {
      return <ChevronsUpDown className="h-3.5 w-3.5 text-gray-400" />;
    } else if (sortState === 'incomplete') {
      return <ArrowUp className="h-3.5 w-3.5 text-blue-600" />;
    } else {
      return <ArrowDown className="h-3.5 w-3.5 text-green-600" />;
    }
  };

  const exportToCSV = () => {
    // Build comprehensive headers
    const headers = [
      'Company Name',
      'POC Email',
      'POC Phone',
      'Role',
      'Categories',
      ...taskColumns.map(t => t.label),
      'Progress (%)',
      'Progress Status',
      'Has Remarks'
    ];

    // Build rows with all company data
    const rows = filteredCompanies.map(company => {
      const row = [
        company.companyName || '',
        company.pocEmail || '',
        company.pocPhone || '',
        getRoleBadge(company.role).text || '',
        // Join categories with semicolons
        company.category && company.category.length > 0
          ? company.category.join('; ')
          : 'None'
      ];

      // Add task completion status
      taskColumns.forEach(col => {
        row.push(company.tasks[col.key] ? 'Yes' : 'No');
      });

      // Add progress data
      row.push(company.progressPercentage.toString());
      row.push(getProgressBadge(company.progressPercentage).text);
      row.push(company.hasRemark ? 'Yes' : 'No');

      return row;
    });

    // Properly escape CSV fields that may contain commas, quotes, or newlines
    const escapeCSVField = (field) => {
      const fieldStr = String(field);
      if (fieldStr.includes(',') || fieldStr.includes('"') || fieldStr.includes('\n') || fieldStr.includes('\r')) {
        return `"${fieldStr.replace(/"/g, '""')}"`;
      }
      return fieldStr;
    };

    // Build CSV content with BOM for Excel compatibility
    const BOM = '\uFEFF';
    const csvContent = BOM + [
      headers.map(escapeCSVField).join(','),
      ...rows.map(row => row.map(escapeCSVField).join(','))
    ].join('\r\n'); // Use \r\n for Windows compatibility

    // Create and download the file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `company-progress-${new Date().toISOString().split('T')[0]}.csv`;
    a.style.display = 'none';
    document.body.appendChild(a);
    a.click();

    // Cleanup
    setTimeout(() => {
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    }, 100);

    toast({
      title: 'Success',
      description: `Exported ${filteredCompanies.length} companies to CSV`,
    });
  };

  const getProgressColor = (percentage) => {
    if (percentage === 100) return 'text-green-600';
    if (percentage >= 60) return 'text-blue-600';
    if (percentage >= 30) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getProgressBadge = (percentage) => {
    if (percentage === 100) return { variant: 'success', text: 'Completed' };
    if (percentage >= 60) return { variant: 'default', text: 'In Progress' };
    if (percentage >= 30) return { variant: 'warning', text: 'Started' };
    return { variant: 'destructive', text: 'Not Started' };
  };

  const getRoleBadge = (role) => {
    const roleConfig = {
      'company': { variant: 'default', text: 'Demo', color: 'bg-blue-100 text-blue-700' },
      'non-demo': { variant: 'secondary', text: 'Non-Demo', color: 'bg-gray-100 text-gray-700' },
      'sponsor': { variant: 'success', text: 'Sponsor', color: 'bg-green-100 text-green-700' },
      'admin': { variant: 'destructive', text: 'Admin', color: 'bg-red-100 text-red-700' }
    };
    return roleConfig[role] || roleConfig.company;
  };

  const handleOpenRemarks = (company) => {
    setSelectedCompanyForRemarks(company);
    setRemarksModalOpen(true);
  };

  const handleCloseRemarks = () => {
    setRemarksModalOpen(false);
    setSelectedCompanyForRemarks(null);
  };

  const handleRemarkUpdated = (companyId, hasRemark) => {
    // Optimistically update the company's hasRemark status without full refresh
    setCompanies(prevCompanies =>
      prevCompanies.map(company =>
        company._id === companyId
          ? { ...company, hasRemark }
          : company
      )
    );
  };

  return (
    <Card className="w-full h-full flex flex-col overflow-hidden">
      <div className="flex-shrink-0 bg-white px-4 py-3 border-b">
        <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-3">
          <div>
            <h2 className="text-xl sm:text-2xl font-bold">Company Progress Tracker</h2>
            <p className="text-sm text-gray-500 hidden sm:block">
              Monitor preparation status for all registered companies
            </p>
          </div>
          <div className="flex gap-2 w-full lg:w-auto">
            <Button
              variant="outline"
              size="sm"
              onClick={fetchCompaniesProgress}
              disabled={loading}
              className="flex-1 lg:flex-none"
            >
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''} lg:mr-2`} />
              <span className="hidden lg:inline">Refresh</span>
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={exportToCSV}
              className="flex-1 lg:flex-none"
            >
              <Download className="h-4 w-4 lg:mr-2" />
              <span className="hidden lg:inline">Export CSV</span>
            </Button>
          </div>
        </div>

        <div className="flex flex-col gap-3 mt-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              placeholder="Search by company name or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            <select
              className="px-3 py-2 border rounded-md text-sm w-full"
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value)}
            >
              <option value="all">All Roles</option>
              <option value="company">Demo Companies</option>
              <option value="non-demo">Non-Demo</option>
              <option value="sponsor">Sponsors</option>
            </select>
            <select
              className="px-3 py-2 border rounded-md text-sm w-full"
              value={progressFilter}
              onChange={(e) => setProgressFilter(e.target.value)}
            >
              <option value="all">All Progress</option>
              <option value="completed">Completed (100%)</option>
              <option value="in-progress">In Progress</option>
              <option value="not-started">Not Started</option>
            </select>
            <select
              className="px-3 py-2 border rounded-md text-sm w-full"
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
            >
              <option value="all">All Categories</option>
              {categories.map((cat) => (
                <option key={cat} value={cat}>
                  {cat}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Mobile Card View */}
      <div className="md:hidden flex-1 overflow-y-auto">
        {loading ? (
          <div className="px-4 py-8 text-center text-gray-500">
            Loading companies...
          </div>
        ) : filteredCompanies.length === 0 ? (
          <div className="px-4 py-8 text-center text-gray-500">
            No companies found
          </div>
        ) : (
          <div className="space-y-3 p-4">
            {filteredCompanies.map((company) => (
              <div key={company._id} className="bg-white border rounded-lg p-4 space-y-3 shadow-sm">
                {/* Company Header */}
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-medium text-sm">{company.companyName}</h3>
                      <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${getRoleBadge(company.role).color}`}>
                        {getRoleBadge(company.role).text}
                      </span>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">{company.pocEmail}</p>
                    {company.category && company.category.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-2">
                        {company.category.slice(0, 1).map((cat, idx) => (
                          <span
                            key={idx}
                            className="px-1.5 py-0.5 text-xs bg-purple-100 text-purple-700 rounded"
                            title={cat}
                          >
                            {cat}
                          </span>
                        ))}
                        {company.category.length > 1 && (
                          <span
                            className="px-1.5 py-0.5 text-xs bg-gray-100 text-gray-600 rounded"
                            title={company.category.slice(1).join(', ')}
                          >
                            +{company.category.length - 1} more
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleOpenRemarks(company)}
                      className="px-2 relative"
                    >
                      <MessageSquare
                        className={`h-4 w-4 ${company.hasRemark ? 'text-blue-600 fill-blue-100' : 'text-gray-400'}`}
                      />
                      {company.hasRemark && (
                        <span className="absolute -top-1 -right-1 h-2 w-2 bg-blue-600 rounded-full"></span>
                      )}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onViewDetails(company)}
                      className="px-2"
                    >
                      <Eye className="h-4 w-4" />
                    </Button>
                  </div>
                </div>

                {/* Progress Bar */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-600">Progress</span>
                    <div className="flex items-center gap-2">
                      <span className={`text-sm font-bold ${getProgressColor(company.progressPercentage)}`}>
                        {company.progressPercentage}%
                      </span>
                      <Badge
                        variant={getProgressBadge(company.progressPercentage).variant}
                        className="text-xs"
                      >
                        {getProgressBadge(company.progressPercentage).text}
                      </Badge>
                    </div>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className={`h-2 rounded-full transition-all ${
                        company.progressPercentage === 100 ? 'bg-green-600' :
                        company.progressPercentage >= 60 ? 'bg-blue-600' :
                        company.progressPercentage >= 30 ? 'bg-yellow-600' :
                        'bg-red-600'
                      }`}
                      style={{ width: `${company.progressPercentage}%` }}
                    />
                  </div>
                </div>

                {/* Task Status Grid */}
                <div className="grid grid-cols-3 gap-2">
                  {taskColumns.map((col) => (
                    <div key={col.key} className="flex items-center gap-2">
                      {company.tasks[col.key] ? (
                        <CheckCircle className="h-4 w-4 text-green-500 flex-shrink-0" />
                      ) : (
                        <Circle className="h-4 w-4 text-gray-300 flex-shrink-0" />
                      )}
                      <span className="text-sm text-gray-600 truncate" title={col.label}>
                        {col.label}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Desktop Table View */}
      <div className="hidden md:block flex-1 overflow-x-auto overflow-y-auto">
        <table className="w-full">
          <thead className="bg-gray-50 border-b sticky top-0 z-30 shadow-md">
            <tr>
              <th className="sticky left-0 bg-gray-50 pl-4 pr-3 py-2.5 text-left text-xs font-medium text-gray-700 uppercase tracking-wide z-40 shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)]">
                Company
              </th>
              {taskColumns.map((col) => (
                <th
                  key={col.key}
                  className="px-3 py-2.5 text-center text-xs font-medium text-gray-700 uppercase tracking-wide whitespace-nowrap cursor-pointer hover:bg-gray-100 transition-colors select-none"
                  title={`Click to sort by ${col.label}`}
                  onClick={() => handleColumnSort(col.key)}
                >
                  <div className="flex items-center justify-center gap-1.5">
                    <span>{col.label}</span>
                    {getSortIcon(col.key)}
                  </div>
                </th>
              ))}
              <th
                className="px-3 py-2.5 text-center text-xs font-medium text-gray-700 uppercase tracking-wide cursor-pointer hover:bg-gray-100 transition-colors select-none"
                title="Click to sort by Progress"
                onClick={() => handleColumnSort(progressColumn.key)}
              >
                <div className="flex items-center justify-center gap-1.5">
                  <span>Progress</span>
                  {getSortIcon(progressColumn.key)}
                </div>
              </th>
              <th className="px-3 py-2.5 text-center text-xs font-medium text-gray-700 uppercase tracking-wide">
                Remarks
              </th>
              <th className="px-3 py-2.5 text-center text-xs font-medium text-gray-700 uppercase tracking-wide">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {loading ? (
              <tr>
                <td colSpan={taskColumns.length + 4} className="px-4 py-8 text-center text-gray-500">
                  Loading companies...
                </td>
              </tr>
            ) : filteredCompanies.length === 0 ? (
              <tr>
                <td colSpan={taskColumns.length + 4} className="px-4 py-8 text-center text-gray-500">
                  No companies found
                </td>
              </tr>
            ) : (
              filteredCompanies.map((company) => (
                <tr key={company._id} className="group hover:bg-gray-50">
                  <td className="sticky left-0 bg-white group-hover:bg-gray-50 pl-4 pr-3 py-3 whitespace-nowrap z-20 shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)]">
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-gray-900">
                          {company.companyName}
                        </span>
                        <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${getRoleBadge(company.role).color}`}>
                          {getRoleBadge(company.role).text}
                        </span>
                      </div>
                      <div className="text-xs text-gray-500 mt-1">{company.pocEmail}</div>
                      {company.category && company.category.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-1.5">
                          {company.category.slice(0, 1).map((cat, idx) => (
                            <span
                              key={idx}
                              className="px-1.5 py-0.5 text-xs bg-purple-100 text-purple-700 rounded"
                              title={cat}
                            >
                              {cat.length > 25 ? cat.substring(0, 25) + '...' : cat}
                            </span>
                          ))}
                          {company.category.length > 1 && (
                            <span
                              className="px-1.5 py-0.5 text-xs bg-gray-100 text-gray-600 rounded"
                              title={company.category.slice(1).join(', ')}
                            >
                              +{company.category.length - 1}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  </td>
                  {taskColumns.map((col) => {
                    // Check if this task is applicable for the company's role
                    const isApplicable = company.applicableTasks?.includes(col.key) ?? true;

                    return (
                      <td key={col.key} className="px-3 py-3 whitespace-nowrap text-center">
                        {!isApplicable ? (
                          // Show dash for non-applicable tasks (non-demo/sponsor only have 3 tasks)
                          <span className="text-gray-400 font-medium text-lg">−</span>
                        ) : company.tasks[col.key] ? (
                          // Show checkmark for completed applicable tasks
                          <CheckCircle className="h-5 w-5 text-green-500 mx-auto" />
                        ) : (
                          // Show circle for pending applicable tasks
                          <Circle className="h-5 w-5 text-gray-300 mx-auto" />
                        )}
                      </td>
                    );
                  })}
                  <td className="px-3 py-3 whitespace-nowrap text-center">
                    <div className="flex flex-col items-center gap-1">
                      <span className={`text-base font-bold ${getProgressColor(company.progressPercentage)}`}>
                        {company.progressPercentage}%
                      </span>
                      <Badge
                        variant={getProgressBadge(company.progressPercentage).variant}
                        className="text-xs"
                      >
                        {getProgressBadge(company.progressPercentage).text}
                      </Badge>
                    </div>
                  </td>
                  <td className="px-3 py-3 whitespace-nowrap text-center">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleOpenRemarks(company)}
                      className="px-2 relative"
                    >
                      <MessageSquare
                        className={`h-5 w-5 ${company.hasRemark ? 'text-blue-600 fill-blue-100' : 'text-gray-400'}`}
                      />
                      {company.hasRemark && (
                        <span className="absolute -top-1 -right-1 h-2 w-2 bg-blue-600 rounded-full"></span>
                      )}
                    </Button>
                  </td>
                  <td className="px-3 py-3 whitespace-nowrap text-center">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onViewDetails(company)}
                      className="px-2"
                    >
                      <Eye className="h-5 w-5" />
                    </Button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {filteredCompanies.length > 0 && (
        <div className="flex-shrink-0 px-4 py-3 bg-gray-50 border-t">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-2 sm:gap-0">
            <p className="text-sm text-gray-700">
              Showing <span className="font-medium">{filteredCompanies.length}</span> of{' '}
              <span className="font-medium">{companies.length}</span> companies
            </p>
            <div className="flex gap-4 text-sm">
              <div className="flex items-center gap-1.5">
                <CheckCircle className="h-4 w-4 text-green-500" />
                <span>Completed</span>
              </div>
              <div className="flex items-center gap-1.5">
                <Circle className="h-4 w-4 text-gray-300" />
                <span>Pending</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Company Remarks Modal */}
      <CompanyRemarksModal
        open={remarksModalOpen}
        onClose={handleCloseRemarks}
        company={selectedCompanyForRemarks}
        onRemarkUpdated={handleRemarkUpdated}
      />
    </Card>
  );
}